import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildSameOriginApiUrl } from '../../../api/client';

export type PayrollProviderId = 'csv' | 'quickbooks';

export type PayrollConnectorStatus = 'available' | 'coming_soon' | 'connected';

export type PayrollConnectorInfo = {
  id: string;
  name: string;
  description: string;
  regions: string;
  queuePriority: number;
  status: PayrollConnectorStatus;
  requested: boolean;
  connectPath: string | null;
};

export type PayrollSettingsState = {
  provider: PayrollProviderId;
  syncOnApprove: boolean;
  exportNotifyEmails: string[];
  connectedConnector?: string;
  quickbooks: {
    connected: boolean;
    realmId?: string;
    connectedAt?: string;
  };
  liveSyncAvailable: boolean;
  lastSyncAt?: string;
  lastSyncError?: string;
  requestedConnectors: string[];
};

export const DEFAULT_PAYROLL_SETTINGS: PayrollSettingsState = {
  provider: 'csv',
  syncOnApprove: false,
  exportNotifyEmails: [],
  quickbooks: { connected: false },
  liveSyncAvailable: false,
  requestedConnectors: []
};

export function buildConnectorConnectUrl(connectorId: string): string {
  return buildSameOriginApiUrl(`/api/payroll/connect/${encodeURIComponent(connectorId)}`);
}

type PayrollConnectPickerProps = {
  connectors: PayrollConnectorInfo[];
  onClose: () => void;
  onRequestConnector: (connectorId: string, note?: string) => Promise<void>;
};

function statusLabel(status: PayrollConnectorStatus, requested: boolean): string {
  if (status === 'connected') return 'Connected';
  if (status === 'available') return 'Available';
  if (requested) return 'Requested';
  return 'Coming soon';
}

function statusClass(status: PayrollConnectorStatus, requested: boolean): string {
  if (status === 'connected') return 'payroll-connector-card payroll-connector-card--connected';
  if (status === 'available') return 'payroll-connector-card payroll-connector-card--available';
  if (requested) return 'payroll-connector-card payroll-connector-card--requested';
  return 'payroll-connector-card';
}

export function PayrollConnectPicker({
  connectors,
  onClose,
  onRequestConnector
}: PayrollConnectPickerProps): React.JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [otherNote, setOtherNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const sorted = useMemo(
    () => [...connectors].sort((a, b) => a.queuePriority - b.queuePriority),
    [connectors]
  );

  const handleConnect = (connector: PayrollConnectorInfo) => {
    if (!connector.connectPath) return;
    window.location.assign(buildConnectorConnectUrl(connector.id));
  };

  const handleRequest = async (connector: PayrollConnectorInfo) => {
    if (busyId) return;
    setBusyId(connector.id);
    setError(null);
    try {
      const note = connector.id === 'other' ? otherNote.trim() : undefined;
      if (connector.id === 'other' && !note) {
        setError('Tell us which payroll system you use.');
        return;
      }
      await onRequestConnector(connector.id, note);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save request.');
    } finally {
      setBusyId(null);
    }
  };

  return createPortal(
    <div className="hours-entry-modal-backdrop payroll-connect-backdrop" onClick={onClose}>
      <div
        className="hours-entry-modal payroll-connect-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payroll-connect-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="payroll-connect-modal-header">
          <div className="hours-entry-modal-head">
            <h3 id="payroll-connect-title">Choose your payroll provider</h3>
            <button type="button" className="hours-entry-modal-close" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
          <p className="settings-hint payroll-connect-modal-hint">
            Connect your company&apos;s payroll account for live sync. Not listed? Request priority — we build
            connectors based on customer demand.
          </p>
        </div>

        <div className="payroll-connect-modal-body">
          {error ? <div className="banner banner-error">{error}</div> : null}

          <div className="payroll-connector-grid">
          {sorted.map((connector) => {
            const label = statusLabel(connector.status, connector.requested);
            return (
              <article key={connector.id} className={statusClass(connector.status, connector.requested)}>
                <div className="payroll-connector-card-head">
                  <strong>{connector.name}</strong>
                  <span className="payroll-connector-badge">{label}</span>
                </div>
                <p className="payroll-connector-desc">{connector.description}</p>
                <p className="payroll-connector-regions">{connector.regions}</p>
                {connector.status === 'available' ? (
                  <button
                    type="button"
                    className="btn-primary payroll-connector-action"
                    onClick={() => handleConnect(connector)}
                  >
                    Connect
                  </button>
                ) : connector.status === 'connected' ? (
                  <p className="settings-hint payroll-connector-connected-hint">Active for this workspace</p>
                ) : connector.requested ? (
                  <p className="settings-hint payroll-connector-connected-hint">On our build queue</p>
                ) : (
                  <>
                    {connector.id === 'other' ? (
                      <input
                        className="form-input payroll-connector-other-input"
                        placeholder="Provider name (e.g. Rippling)"
                        value={otherNote}
                        onChange={(e) => setOtherNote(e.target.value)}
                      />
                    ) : null}
                    <button
                      type="button"
                      className="dash-btn dash-btn-ghost payroll-connector-action"
                      disabled={busyId === connector.id}
                      onClick={() => void handleRequest(connector)}
                    >
                      {busyId === connector.id ? 'Saving…' : 'Request priority'}
                    </button>
                  </>
                )}
              </article>
            );
          })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
